import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, FormControlStatus, FormGroupDirective, NgControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isNil } from 'lodash';
import { tap } from 'rxjs/operators';

import { getActiveValidationErrorFunction } from '@shared/functions/get-active-validation-error.function';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { CustomControlAbstract } from '@ui-components/controls/custom-control.abstract';

@UntilDestroy()
@Component({
  selector: 'app-radio-button',
  templateUrl: './radio-button.component.html',
  styleUrls: ['./radio-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RadioButtonComponent extends CustomControlAbstract<any> implements OnInit, ControlValueAccessor {
  disabled = false;

  @Input() label = '';
  @Input() labelCss = 'nowrap body-small-bold';
  @Input() optionCss = 'body-small';
  @Input() items: IRadioButtonOption<unknown>[] = [];
  @Input() labelRequired = false;
  @Input() radioGroupClass = 'display-flex';
  @Input() errorSection = true;
  @Input() markAsInvalid = false;
  @Input() showClearControl = false;
  @Input() defaultValue: unknown = null;
  @Input() isRequired = false;
  @Input() clearTooltip = 'Clear selection';
  @Input() clearToDefault = true;
  @Output() resetToDefaultEvent = new EventEmitter<void>();

  constructor(
    @Self() @Optional() protected ngControl: NgControl,
    protected cdr: ChangeDetectorRef,
    @Optional() formDirective: FormGroupDirective
  ) {
    super(ngControl, cdr, formDirective);
  }

  public shouldShowClear(optionValue: unknown): boolean {
    if (!this.showClearControl) return false;
    if (this.control?.value !== optionValue) return false;

    return this.control?.value !== null && this.control?.value !== undefined;
  }

  public onResetClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.control?.setValue(null);
    this.control?.markAsTouched();
    this.control?.markAsDirty();
    this.resetToDefaultEvent.emit();
  }

  @Input() set attrDisable(attrDisable: boolean) {
    if (attrDisable) {
      this.control.disable();
    } else {
      this.control.enable();
    }
    this.disabled = attrDisable;
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    this.checkIfEmpty();
    this.control.valueChanges
      .pipe(
        untilDestroyed(this),
        tap((value: any) => {
          this.setValue(value);
          this.checkIfEmpty();
        })
      )
      .subscribe()
      .untilDestroyed(this);

    this.control.statusChanges
      .pipe(
        untilDestroyed(this),
        tap(status => this.checkControlStatus(status))
      )
      .subscribe()
      .untilDestroyed(this);

    if (this.ngControl?.control) {
      if (this.ngControl.control.errors) {
        this.errors = { ...this.ngControl.control.errors };
      }
      this.checkStatus();
      this.ngControl.control.statusChanges
        .pipe(
          untilDestroyed(this),
          tap(status => this.checkControlStatus(status))
        )
        .subscribe()
        .untilDestroyed(this);
    }
  }

  writeValue(value: any): void {
    this.control.setValue(value);
  }

  setValue(value: unknown) {
    if (this.ngControl.control.disabled) {
      return;
    }
    this.onChanged(value);
    this.onTouched();
  }

  protected checkControlStatus(status: FormControlStatus) {
    super.checkControlStatus(status);
    this.checkIfEmpty();
  }

  /**
   *
   * @private
   * This method checks empty value if labelRequired params is set as true.
   * Its only visual designation - does not change the status of the control as valid or invalid
   */

  private checkIfEmpty() {
    const isEmpty = isNil(this.control?.value);
    this.isEmptyValue = this.labelRequired ? isEmpty : false;
    this.errors = getActiveValidationErrorFunction({
      ...this.ngControl?.control?.errors,
      ...{ required: this.isEmptyValue },
    });
    this.cdr.detectChanges();
  }
}
